/**
 * useVisualViewportHeight Hook
 * Tracks the visual viewport height, useful for keyboard detection on mobile
 */
import { useState, useEffect } from 'react';
import { supportsVisualViewport } from '../utils/deviceDetection';

/**
 * Hook to track the visual viewport height
 * Uses the Visual Viewport API when available, falls back to window.innerHeight
 * Useful for detecting when on-screen keyboard appears/disappears on mobile
 *
 * @returns current visual viewport height in pixels
 *
 * @example
 * const viewportHeight = useVisualViewportHeight();
 * const keyboardHeight = window.innerHeight - viewportHeight;
 * if (keyboardHeight > 100) {
 *   // Keyboard is likely visible
 * }
 */
export function useVisualViewportHeight(): number {
  const [height, setHeight] = useState<number>(() => {
    if (typeof window === 'undefined') return 0;

    // Try to use Visual Viewport API first
    if (supportsVisualViewport() && window.visualViewport) {
      return window.visualViewport.height || window.innerHeight;
    }

    // Fallback to window.innerHeight
    return window.innerHeight;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Initial set to ensure correct value
    const updateHeight = () => {
      if (supportsVisualViewport() && window.visualViewport) {
        setHeight(window.visualViewport.height || window.innerHeight);
      } else {
        setHeight(window.innerHeight);
      }
    };

    // Initial update
    updateHeight();

    // If Visual Viewport API is available, listen to its resize event
    if (supportsVisualViewport() && window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateHeight);
      // Also listen for scroll events on visual viewport
      window.visualViewport.addEventListener('scroll', updateHeight);
    }

    // Also listen to window resize as fallback
    window.addEventListener('resize', updateHeight);

    // Cleanup
    return () => {
      if (supportsVisualViewport() && window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateHeight);
        window.visualViewport.removeEventListener('scroll', updateHeight);
      }
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  return height;
}
